// lib
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { RouteComponentProps } from 'react-router'

// components
import EventsFeedCalendarSubheader from 'src/components/header/subheaders/pages/events_feed/calendar_subheader'
import MetaTags from 'src/components/meta_tags/meta_tags'
import CategorizedEventFeed from 'src/containers/smart_components/feeds/categorized_event_feed'
import StandardPageTitle from 'src/containers/smart_components/page_title/page_title'
import { getPageTitle } from 'src/utils/page_title/page_title'
import { LayoutFeaturedContainer } from '../layout/layout_featured_container'

// constants & interfaces
import { EVENT_FEED_DEFAULT_PER_CATEGORY, FEED_DEFAULT_PER_PAGE, FEED_TYPE_EVENT } from 'src/constants/feed'
import {
  EVENTSFEED_DESCRIPTION,
  EVENTSFEED_OG_IMAGE,
  EVENTSFEED_TITLE
} from 'src/constants/meta_tags'
import { textResources } from 'src/lang/de'

// helpers
import { DateTimeRangeInput, useEventsOverview } from '@lopo-tech/lopo-api'
import { PREVIEW_IMAGE_HEIGHT, PREVIEW_IMAGE_WIDTH } from 'src/constants/image'
import { useIsPostCreateOpen, usePageTracking } from 'src/pages/hooks/hooks'
import { useGetAppLocationShape, useGetBoundingBox } from 'src/reducers/app-state/app-state_hooks'
import { useSelectUser } from 'src/reducers/user/user_hooks'
import { EventBannerGroup } from '../components/feed/layouts/banner_feed/banner_feed'
import { useSelectDateFilter } from '../reducers/filters/filters_hooks'
import DateFormatter from '../utils/date_formatter/date_formatter'
import { createMarkersList } from '../utils/feeds'

const CategorizedEventFeedPage: React.FC<RouteComponentProps> = ({ match }) => {
  const locationShape = useGetAppLocationShape()
  const boundingBox = useGetBoundingBox()
  const user = useSelectUser()

  const isPostCreateOpen = useIsPostCreateOpen(match)

  const title = locationShape && EVENTSFEED_TITLE(locationShape.name)
  const description = locationShape && EVENTSFEED_DESCRIPTION(locationShape.name)

  const dateFilterRange = useSelectDateFilter()
  const dateFilterStart = dateFilterRange.start ?? DateFormatter.getToday()
  const dateFilterEnd = dateFilterRange.end ?? DateFormatter.endOfDay(dateFilterStart)

  const moreEventsTimeRange: DateTimeRangeInput = useMemo(
    () => {
      const startOfSuggestionRange = DateFormatter.startOfDay(DateFormatter.getNextDay(dateFilterEnd))

      return {
        startTime: startOfSuggestionRange
      }
    },
    [dateFilterEnd, dateFilterStart]
  )

  const { data, loading, fetchMore } = useEventsOverview({
    variables: {
      boundingBox: boundingBox!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      locationShapes: [locationShape?.id!],
      categorizedTimeRanges: {
        endTime: dateFilterEnd,
        startTime: dateFilterStart
      },
      listTimeRanges: moreEventsTimeRange,
      eventsPerCategory: EVENT_FEED_DEFAULT_PER_CATEGORY,
      page: 1,
      perPage: FEED_DEFAULT_PER_PAGE,
      width: PREVIEW_IMAGE_WIDTH,
      height: PREVIEW_IMAGE_HEIGHT
    },
    skip: !locationShape || !boundingBox
  })

  // we need to ensure that the callback always has the latest loading state
  // to avoid multiple fetchMore calls being triggered due to the getNextPage
  // being in a throttled fashion
  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const getNextPage = useCallback(() => {
    if (data?.events.pageInfo.nextPage && !loadingRef.current) {
      fetchMore({
        variables: { page: data.events.pageInfo.nextPage }
      })
    }
  }, [data?.events.pageInfo.nextPage, fetchMore])

  const eventItems = useMemo(() => {
    const listEventData = data?.events?.items ?? []
    const categorizedEventData = data?.categorizedEvents?.flatMap(c => c.events ?? []) ?? []
    return [...listEventData, ...categorizedEventData]
  }, [data?.events?.items, data?.categorizedEvents])

  const markers = createMarkersList(eventItems)

  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set())

  const onVisibleChange = useCallback((id: string, inView: boolean) => {
    setVisibleIds(prev => {
      const next = new Set(prev)
      if (inView) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const markersInView = useMemo(
    () => markers.filter(m => visibleIds.has(m.id)),
    [markers, visibleIds]
  )

  const DUMMY_CATEGORY = { name: '', slug: '' }

  const moreEvents: EventBannerGroup[] = data?.events
    ? [{ category: DUMMY_CATEGORY, events: data.events.items }]
    : []

  usePageTracking({ pageTitle: title, user })

  return (
    <>
      <MetaTags
        description={description}
        geo_placename={locationShape?.name}
        geo_position={locationShape && `${locationShape.center.latitude};${locationShape.center.longitude}`}
        og_image={EVENTSFEED_OG_IMAGE}
        og_url={document.location.toString()}
        title={title}
      />
      <LayoutFeaturedContainer
        markers={markersInView}
        locationShape={locationShape}
        subHeader={<EventsFeedCalendarSubheader />}
        activeNavigationItem={FEED_TYPE_EVENT}
        pageTitle={
          <StandardPageTitle
            text={getPageTitle(textResources.pageTitles.eventfeedSimple, textResources.pageTitles.eventfeed)}
            showLocation />
        }
        withHeaderMargin>
        <CategorizedEventFeed
          getNextPage={getNextPage}
          loading={loading}
          eventFeedData={data?.categorizedEvents ?? []}
          isPostCreateOpen={isPostCreateOpen}
          onVisibleChange={onVisibleChange}
          moreEvents={moreEvents}
        />
      </LayoutFeaturedContainer>
    </>
  )
}

export default CategorizedEventFeedPage
